import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Message } from './models/messages/messages';
import { NotificationService } from './services/NotificationService/NotificationService';
import { store, useAppDispatch } from './store';
import {
  notificationActions,
  notificationsSelectors,
} from './store/notifications';
import { settingSelectors } from './store/settings';
import { userSelectors } from './store/user';
import { authSelectors } from './store/auth';
import Loader from './components/loader/loader';

type IntialNotificationSetupContextValues = {
  startService: () => void;
  stopService: () => void;
};

export const IntialNotificationSetupContext =
  React.createContext<IntialNotificationSetupContextValues>(
    {} as IntialNotificationSetupContextValues
  );

const InitialNotificationSetup: React.FC = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const isMounted = useRef(true);

  const dispatch = useAppDispatch();

  const user = useSelector(userSelectors.getUser);
  const auth = useSelector(authSelectors.getAuthUser);

  const notificationReferences = useSelector(
    notificationsSelectors.getAllNotificationReferences
  );
  const notificationPollInterval = useSelector(
    settingSelectors.getNotificationPollInterval
  );
  // const { initloading: initLoading } = useStoreSetup();
  const notificationServiceRef = useRef<NotificationService | undefined>(
    undefined
  );

  useEffect(() => {
    isMounted.current = true;
    initializeServices();
    return () => {
      isMounted.current = false;
      stopService();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNotificationsRecieved = useCallback(
    (messages: Message[]) => {
      if (!isMounted.current) return;
      const newMessages = messages.filter(
        (message) => !notificationReferences.includes(message.reference)
      );

      if (newMessages.length > 0) {
        dispatch(notificationActions.addNotifications(newMessages));
      }
    },
    [dispatch, notificationReferences]
  );

  const initializeServices = useCallback(() => {
    if (!notificationServiceRef.current) {
      notificationServiceRef.current = new NotificationService(
        notificationPollInterval,
        auth?.auth_token,
        user
      );
    }
    notificationServiceRef.current.registerValidators(store);

    if (isMounted.current) setIsLoading(true);

    notificationServiceRef.current.onNotificationsReceived = (
      messages: Message[]
    ) => {
      if (isMounted.current) {
        setIsLoading(false);
        onNotificationsRecieved(messages);
      }
    };
    notificationServiceRef.current.initialEvaluate();
    notificationServiceRef.current.start();
  }, [
    auth?.auth_token,
    notificationPollInterval,
    onNotificationsRecieved,
    user,
  ]);

  const stopService = (isUnmount = false) => {
    if (notificationServiceRef.current) {
      notificationServiceRef.current.stop();
      if (!isUnmount && isMounted.current) {
        dispatch(notificationActions.resetFrontendNotificationState());
      }
    }
  };

  const startService = () => {
    if (notificationServiceRef.current) {
      initializeServices();
    }
  };

  return (
    <IntialNotificationSetupContext.Provider
      value={{ stopService, startService }}
    >
      {!isLoading && children}
      {isLoading && <Loader loadingMessage="Loading notifications ..." />}
    </IntialNotificationSetupContext.Provider>
  );
};

export default InitialNotificationSetup;
